/**
 * Query-parameter softlock fixture. This room is not player content: it is the
 * permanent regression evidence that a genuinely sealed enclosure is reported
 * as a trap while a deliberately narrow three-wall pocket remains escapable.
 */

import * as THREE from 'three';
import { surface } from '@/models/materials';

const FLOOR = 0x8f8f8f;
const WALL = 0x33373c;

export interface SoftlockFixtureSolid {
  name: string;
  half: { x: number; y: number; z: number };
  pos: { x: number; y: number; z: number };
  colour: number;
}

/** The sealed cell's centre: the regression gate must identify this as a trap. */
export const SOFTLOCK_TRAP = { x: -3, y: 1.2, z: 0 } as const;
/** The three-wall pocket's centre: it is tight, but its +Z opening must clear. */
export const SOFTLOCK_ESCAPE = { x: 3, y: 1.2, z: 0 } as const;
/** Open ground gives the query-only room a harmless default spawn. */
export const SOFTLOCK_FIXTURE_SPAWN = { x: 0, y: 1.2, z: 5 } as const;

/** Meshes and colliders are both made from this list: no phantom fixture geometry. */
export function softlockFixtureSolids(): SoftlockFixtureSolid[] {
  return [
    {
      name: 'ground',
      half: { x: 8, y: 0.05, z: 8 },
      pos: { x: 0, y: -0.05, z: 0 },
      colour: FLOOR,
    },
    // A 1.4 m square interior: room to settle and walk, and no way out. She
    // covers 0.565 m against each wall from the centre, which is exactly why
    // the old 0.15 m displacement rule called this cell escapable (D-055).
    { name: 'trap-north', half: { x: 0.8, y: 0.6, z: 0.1 }, pos: { x: -3, y: 0.6, z: 0.8 }, colour: WALL },
    { name: 'trap-south', half: { x: 0.8, y: 0.6, z: 0.1 }, pos: { x: -3, y: 0.6, z: -0.8 }, colour: WALL },
    { name: 'trap-east', half: { x: 0.1, y: 0.6, z: 0.8 }, pos: { x: -2.2, y: 0.6, z: 0 }, colour: WALL },
    { name: 'trap-west', half: { x: 0.1, y: 0.6, z: 0.8 }, pos: { x: -3.8, y: 0.6, z: 0 }, colour: WALL },
    // A 0.8 m wide slot, blind to the south, open to +Z. She gets 2.014 m out of
    // the mouth and 0.265 m against either side, so it separates cleanly from the
    // sealed cell without being generous.
    { name: 'escape-east', half: { x: 0.1, y: 0.6, z: 0.9 }, pos: { x: 3.5, y: 0.6, z: -0.1 }, colour: WALL },
    { name: 'escape-west', half: { x: 0.1, y: 0.6, z: 0.9 }, pos: { x: 2.5, y: 0.6, z: -0.1 }, colour: WALL },
    { name: 'escape-south', half: { x: 0.6, y: 0.6, z: 0.1 }, pos: { x: 3, y: 0.6, z: -1 }, colour: WALL },
  ];
}

export function createSoftlockFixtureRoom(): THREE.Group {
  const room = new THREE.Group();
  room.name = 'softlock-fixture';
  for (const solid of softlockFixtureSolids()) {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(solid.half.x * 2, solid.half.y * 2, solid.half.z * 2),
      surface('mineral', solid.colour),
    );
    mesh.name = solid.name;
    mesh.position.set(solid.pos.x, solid.pos.y, solid.pos.z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    room.add(mesh);
  }
  return room;
}
